<template>
  <div>
    <my-header></my-header>

    <div class="gvnts-box">
      <div class="h-panel">
        <b-button v-b-modal.help-modal>Help</b-button>

        <b-button
          v-if="mayStartGvnt"
          :to="{name: 'new_gvnt'}"
          variant="primary"
        >
          New game event
        </b-button>

        <b-button
          :disabled="isDeleteGvntsButtonDisabled"
          :variant="deleteGvntsButtonVariant"
          @click="deleteGvntsWithConfirmation"
        >
          Delete
        </b-button>
      </div>

      <h2>Game events</h2>

      <template v-if="isGvntsVisible">
        <gvnts-plot
          :gvnts="gvnts"
          v-on:gvnt-clicked="showGvntInfo"
        />

        <gvnt-table
          :gvntContainers="gvntContainers"
          :nowSec="nowSec"
          :mayManageGvnts="mayManageGvnts"
          @gvnts-selection-requested="selectGvnts"
          @gvnts-deselection-requested="deselectGvnts"
          @gvnt-run-for-all-requested="runGvntForAllWithConfirmation"
          @gvnt-finalization-requested="finalizeGvntWithConfirmation"
          @gvnt-deletion-requested="deleteGvntWithConfirmation"
        />
      </template>


      <p v-if="message !== null">
        {{message}}
      </p>

      <loading-indicator v-if="isLoading"/>

    </div>

    <b-modal id="help-modal" title="Help" ok-only ok-title="Close">
      <div class="help">
        <p>
          На данной странице показаны все игровые события (game events).
        </p>
        <p>
          Игровое событие может быть в одном из трех состояний.
        </p>
        <ol>
          <li>приближающееся (incoming)</li>
          <li><span class="active-gvnt">активное (active)</span></li>
          <li>завершенное (finished)</li>
        </ol>

        <h5 class="mt-3">Публичное игровое событие</h5>
        <p>
          Обычные игроки (normal players) — это все игроки кроме QA.
        </p>
        <p>
          Публичное игровое событие (public game event) — игровое событие, которое доступно для обычных игроков (а&nbsp;не&nbsp;только для QA).  
        </p>
        <p>
          Публичное игровое событие доступно или для всех игроков (all), или только для обыных игроков (normal players).
        </p>
      </div>
    </b-modal>

  </div>
</template>

<script>
/**
 * @typedef { import('../auth.js').Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { PLAYER_GROUP_ID_ALL } from '../model/event.js';

/**
 * @typedef { import('../model/gvnt.js').GvntPreview } GvntPreview
 */
import { GVNT_STATUS_FINISHED } from '../model/gvnt.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { mayManageGvnts as utilMayManageGvnts } from '../util.js';
import { mayStartGvnt   as utilMayStartGvnt   } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import GvntsPlot from './GvntsPlot.vue';
import GvntTable from './GvntTable.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'Gvnts',

  components: {
    MyHeader,
    LoadingIndicator,
    GvntsPlot,
    GvntTable,
  },

  data() {
    return {
      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {GvntPreviewContainer[] | null} */
      gvntContainers: null,

      /** @type {?number} */
      timerId: null,

      nowSec: Date.now() / 1000,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {GvntPreview[] | null} */
    gvnts() {
      const containers = this.gvntContainers;
      if(containers === null) {
        return null;
      }

      const gvnts = [];

      for(const container of containers) {
        gvnts.push(container.gvnt);
      }

      return gvnts;
    },

    /** @returns {?string} */
    message() {
      const conts = this.gvntContainers;
      if(conts !== null && conts.length === 0) {
        return 'No game events.';
      }

      return null;
    },

    /** @returns {number[] | null} */
    selectedGvntIds() {
      const containers = this.gvntContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.gvnt.id);
        }
      }

      return ids;
    },

    /** @returns {boolean} */
    hasSelectedGvnts() {
      const containers = this.gvntContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }

      return false;
    },

    /** @returns {boolean} */
    isDeleteGvntsButtonDisabled() {
      return !this.hasSelectedGvnts;
    },

    /** @returns {string} */
    deleteGvntsButtonVariant() {
      if(this.hasSelectedGvnts) {
        return 'danger';
      } else {
        return 'light';
      }
    },

    /** @returns {boolean} */
    isGvntsVisible() {
      const conts = this.gvntContainers;
      return conts !== null && conts.length > 0;
    },

    /** @returns {boolean} */
    mayManageGvnts() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayManageGvnts(actor.role);
    },

    /** @returns {boolean} */
    mayStartGvnt() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayStartGvnt(actor.role);
    },
  },

  created() {
    this.timerId = setInterval(this.updateTime, 1000);

    this.loadGvnts();
  },

  beforeDestroy() {
    if(this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  },

  methods: {
    updateTime() {
      this.nowSec = Date.now() / 1000;
    },

    loadGvnts() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/gvnts';
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const gvnts = data;
        gvnts.sort((a, b) => b.stime - a.stime);

        this.gvntContainers = convertGvntPreviewsToGvntPreviewContainers(gvnts);

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    /**
     * @param {number} gvntId
     */
    showGvntInfo(gvntId) {
      this.$router.push({name: 'gvnt_info', params: {gvntId: gvntId}});
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectGvnts() {
      for(const container of this.gvntContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectGvnts() {
      for(const container of this.gvntContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------

    /**
     * @param {GvntPreview} gvnt
     */
    runGvntForAllWithConfirmation(gvnt) {
      this.confirmRunningGvntForAll(() => {
        this.runForAll(gvnt);
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRunningGvntForAll(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to run for all this game event {env}?',
        'Run',
        onConfirmed,
      );
    },

    /**
     * @param {GvntPreview} gvnt
     */
    runForAll(gvnt) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/gvnt/${gvnt.id}/run_for_all`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        gvnt.playerGroupId = PLAYER_GROUP_ID_ALL;
        notifier.showSuccess('Gvnt runned for all.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    /**
     * @param {GvntPreview} gvnt
     */
    mustShowStartsManyTimesAllButton(gvnt) {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      if(!utilMayManageGvnts(actor.role)) {
        return false;
      }
      return gvnt.startsOnlyOnce;
    },

    /**
     * @param {GvntPreview} gvnt
     */
    setGvntStartsManyTimesWithConfirmation(gvnt) {
      this.confirmSettingGvntStartsManyTimes(() => {
        this.startsManyTimes(gvnt);
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmSettingGvntStartsManyTimes(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to make this game event available to start many times {env}?',
        'Set',
        onConfirmed,
      );
    },

    /**
     * @param {GvntPreview} gvnt
     */
    startsManyTimes(gvnt) {
      notifier.deleteSenderNotifications(this);

      const url = `/gvnt/${gvnt.id}/starts_many_times`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        gvnt.startsOnlyOnce = false;
        notifier.showSuccess('Gvnt can start many times for player.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    /**
     * @param {GvntPreview} gvnt
     */
    finalizeGvntWithConfirmation(gvnt) {
      this.confirmGvntFinalization(() => {
        this.finalizeGvnt(gvnt);
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmGvntFinalization(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to finish this game event {env}?',
        'Finish',
        onConfirmed,
      );
    },

    /**
     * @param {GvntPreview} gvnt
     */
    finalizeGvnt(gvnt) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/gvnt/${gvnt.id}/finalize`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        gvnt.frtime = data.frtime;
        gvnt.status = GVNT_STATUS_FINISHED;
        notifier.showSuccess('Game event finished.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    /**
     * @param {GvntPreview} gvnt
     */
    deleteGvntWithConfirmation(gvnt) {
      notifier.deleteSenderNotifications(this);

      this.confirmGvntDeletion(() => {
        this.deleteGvnt(gvnt);
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmGvntDeletion(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to delete this game event {env}?',
        'Delete',
        onConfirmed,
      );
    },

    /**
     * @param {GvntPreview} gvnt
     */
    deleteGvnt(gvnt) {
      notifier.deleteSenderNotifications(this);

      const conts = this.gvntContainers;
      if(conts === null) {
        notifier.showError('Can not delete game event. conts === null.', {sender: this});
        return;
      }

      const url = `/gvnt/${gvnt.id}/del`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        deleteGvntContainerFromArray(conts, gvnt.id);
        notifier.showSuccess('Game event deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    deleteGvntsWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const gvntIds = this.selectedGvntIds;

      this.confirmGvntsDeletion(
        gvntIds,
        () => {
          this.deleteGvnts(gvntIds);
        },
      );
    },

    /**
     * @param {number[]} gvntIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmGvntsDeletion(gvntIds, onConfirmed) {
      const params = {};
      if(gvntIds.length > 1) {
        params.description = getGvntIdsInfo(gvntIds);
      }

      confirmAction(
        this,
        getGvntsDeletionConfirmationMessage(gvntIds),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} gvntIds */
    deleteGvnts(gvntIds) {
      const idsStr = gvntIds.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/gvnts/delete', data)
      .then(() => {
        deleteGvntContainersFromArray(this.gvntContainers, gvntIds);

        const message = getDeletionCompleteMessage(gvntIds.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};

/**
 * @typedef {Object} GvntPreviewContainer
 * @property {GvntPreview} gvnt
 * @property {boolean}     selected
 */

/**
 * @param {GvntPreview[]} gvnts
 * @returns {GvntPreviewContainer[]}
 */
function convertGvntPreviewsToGvntPreviewContainers(gvnts) {
  const containers = [];

  for(const gvnt of gvnts) {
    const container = {
      gvnt: gvnt,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}

/**
 * @param {GvntPreviewContainer[]} gvntContainers 
 * @param {number[]} gvntIds
 */
function deleteGvntContainersFromArray(gvntContainers, gvntIds) {
  for(const gvntId of gvntIds) {
    deleteGvntContainerFromArray(gvntContainers, gvntId);
  }
}

/**
 * @param {GvntPreviewContainer[]} gvntContainers 
 * @param {number} gvntId
 */
 function deleteGvntContainerFromArray(gvntContainers, gvntId) {
  const index = getGvntContainerIndex(gvntContainers, gvntId);
  if(index !== null) {
    gvntContainers.splice(index, 1);
  }
}

/**
 * @param {GvntPreviewContainer[]} gvntContainers
 * @param {number} gvntId
 * @returns {number | null}
 */
function getGvntContainerIndex(gvntContainers, gvntId) {
  for(let i = 0; i < gvntContainers.length; i++) {
    const container = gvntContainers[i];
    if(container.gvnt.id == gvntId) {
      return i;
    }
  }

  return null;
}

function getGvntIdsInfo(ids) {
  return 'Game event ids: ' + ids.join(', ') + '.';
}

function getGvntsDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getGvntDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} game events {env}?`;
}

function getGvntDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete game event ${id} {env}?`
}

function getDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Game event deleted.'
  } else {
    return 'Game events deleted.'
  }
}
</script>

<style scoped>
.gvnts-box {
  padding-top: 1em;
  margin-left: 1em;
  margin-bottom: 1rem;
}
</style>